import { Organization, SaveOrganization } from 'model/organization';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { validatePhone, validateOrganizationForm } from 'validators';

/**
 * Initial state for organization form
 */
const INITIAL_FORM_STATE: SaveOrganization = {
    name: '',
    nicknameDba: '',
    taxId: '',
    businessType: '',
    email: '',
    phone: '',
    website: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    zipcode: '',
    country: '',
    parentOrganizationId: undefined
};

interface UseOrganizationFormProps {
    accounts: Organization[];
    isAddAccountModalOpen?: boolean;
    handleCloseModal?: () => void;
    editingAccount?: SaveOrganization | null;
    handleSaveChildOrganizationAccount: (accountData: SaveOrganization, closeModalAfterSave?: boolean) => Promise<void>;
    isParentOrganization?: boolean; // True when editing the parent organization itself
    closeModalAfterSave?: boolean; // Whether to close modal after successful save (default: true)
}

export const useOrganizationForm = ({
    accounts,
    isAddAccountModalOpen,
    handleCloseModal,
    editingAccount,
    handleSaveChildOrganizationAccount,
    isParentOrganization = false,
    closeModalAfterSave = true
}: UseOrganizationFormProps) => {
    const isSaving = useSelector((state: RootState) => state.organizations.loading);

    const [formData, setFormData] = useState<SaveOrganization>(INITIAL_FORM_STATE);

    const [errors, setErrors] = useState<Partial<SaveOrganization>>({});
    const [showExitPrompt, setShowExitPrompt] = useState(false);

    // Populate form when account is provided for editing, or reset when switching to add mode
    useEffect(() => {
        if (editingAccount) {
            // Edit mode: populate form with editing account data
            setFormData({
                name: editingAccount.name || '',
                nicknameDba: editingAccount.nicknameDba || '',
                taxId: editingAccount.taxId || '',
                businessType: editingAccount.businessType || '',
                email: editingAccount.email || '',
                phone: editingAccount.phone || '',
                website: editingAccount.website || '',
                addressLineOne: editingAccount.addressLineOne || '',
                addressLineTwo: editingAccount.addressLineTwo || '',
                city: editingAccount.city || '',
                state: editingAccount.state || '',
                zipcode: editingAccount.zipcode || '',
                country: editingAccount.country || '',
                parentOrganizationId: editingAccount.parentOrganizationId || undefined
            });
        } else {
            // Add mode: reset to initial state
            setFormData(INITIAL_FORM_STATE);
            setErrors({});
        }
    }, [editingAccount]);

    const { businessTypes } = useSelector((state: RootState) => state.masterData.data);
    const organizations = useSelector((state: RootState) => state.organizations.organizations);

    // Get parent organization (where parentOrganizationId is null)
    const parentOrganization = organizations.find(
        (org: Organization) => org.parentOrganizationId === null || org.parentOrganizationId === undefined
    );
    const organizationCountry = parentOrganization?.country || 'US';

    const businessTypeOptions = businessTypes.map((type: { id: string; name: string }) => ({
        label: type.name,
        value: type.id
    }));

    const parentOrganizations = accounts.map(
        (acc: { id: number; name: string; parentOrganizationId?: number | null }) => ({
            label: acc.name,
            value: acc.id.toString(),
            parentOrganizationId: acc.parentOrganizationId?.toString() || null
        })
    );

    const validatePhoneField = (phoneValue: string) => {
        const phoneError = validatePhone(phoneValue, organizationCountry);

        setErrors(prev => {
            const newErrors = { ...prev };
            if (phoneError) {
                newErrors.phone = phoneError;
            } else {
                delete newErrors.phone;
            }
            return newErrors;
        });
    };

    const handlePhoneBlur = () => {
        validatePhoneField(formData.phone || '');
    };

    const handleInputChange = (field: keyof SaveOrganization, value: string | boolean | number | undefined) => {
        // Convert string to number for parentOrganizationId if needed
        let finalValue = value;
        if (field === 'parentOrganizationId' && typeof value === 'string') {
            finalValue = value ? Number(value) : undefined;
        }

        setFormData(prev => ({
            ...prev,
            [field]: finalValue
        }));

        // Validate phone field as user types
        if (field === 'phone' && typeof value === 'string') {
            validatePhoneField(value);
            return; // Don't clear error - let validation handle it
        }

        // Clear error when user starts typing (except for phone)
        if (errors[field as keyof Partial<SaveOrganization>]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field as keyof Partial<SaveOrganization>];
                return newErrors;
            });
        }
    };

    const validateForm = (): boolean => {
        // Parent organization is required for all organizations EXCEPT when editing the parent org itself
        // Use the explicit isParentOrganization flag for accurate determination
        const requireParentOrganization = !isParentOrganization;

        // Use centralized validation
        const newErrors = validateOrganizationForm(formData, organizationCountry, requireParentOrganization);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (validateForm()) {
            // Wait for the save operation to complete before closing
            await handleSaveChildOrganizationAccount(formData, closeModalAfterSave);
        }
    };

    /**
     * Reset form to initial state
     * Clears all form data, errors, and exit prompt
     */
    const resetForm = useCallback(() => {
        setFormData(INITIAL_FORM_STATE);
        setErrors({});
        setShowExitPrompt(false);
    }, []);

    return {
        formData,
        setFormData,
        errors,
        setErrors,
        isSaving,
        handleInputChange,
        handlePhoneBlur,
        handleSave,
        showExitPrompt,
        setShowExitPrompt,
        parentOrganizations,
        parentOrganization,
        businessTypeOptions,
        editingAccount,
        handleCloseModal,
        isAddAccountModalOpen,
        resetForm
    };
};

export default useOrganizationForm;
